import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { GoogleGenerativeAI } from '@google/generative-ai';
import { fetchUserDetails, fetch7DayFoodData } from '../services/api';
import { getAvailableTools, toolDeclarations } from '../services/tools';

const getSystemPrompt = (fullHealthData, userDetails, currentTimeString) => {
    // This function encapsulates your detailed prompt logic
    return `

      ### Tool Usage Mandate
      You are provided with a set of tools to perform actions like logging data or scheduling. When a user's request directly matches a tool's capability, you **MUST** call that tool. **Do not** answer conversationally or simulate the action in text.
      - **Example 1:** If the user says "I drank 500ml of water", you **MUST** call the \`log_water\` function with the argument \`{ "amount": "500" }\`. Only after the tool returns a success message should you confirm the action to the user.
      - **Example 2:** If the user asks "Can you book a doctor's call for tomorrow at 3 pm?", you **MUST** use the \`schedule_doctor_call\` tool. Do not reply with "Sure, I can help with that." as your first response. Execute the tool.

      ### Current Context & Data Mandate
    Water Log :
    For logging water use tool log_water—add the user's amount (e.g., “250ml”) as the amount parameter of that tool.  
    Information Gathering: If details are missing, ask concise, combined questions conversationally (“How much water did you have?”).

    Weight Log :
    For logging weight use tool log_weight—pass the user's weight in kilograms as the weight parameter.  
    Information Gathering: If weight isn't provided, ask “What's your current weight in kg?” before calling the tool.

    Sleep Log :
    For logging sleep use tool log_sleep—pass the total sleep duration in minutes as the duration parameter.  
    Information Gathering: If duration is missing, ask “How many minutes did you sleep?” before calling the tool.

    Activity Log :
    For logging activity use tool log_activity—supply activity_name, duration (minutes), and optional intensity (“easy,” “moderate,” “hard”).  
    Information Gathering: If any detail is missing, ask “What activity did you do, for how long, and at what intensity?”

    Food Log :
    For logging individual food items use tool log_food—provide food_name, meal_type (e.g., Breakfast, Lunch), and optional amount.  
    Information Gathering: If missing, ask “Which meal was this and how much did you have?”

    Coach Call Scheduling :
    Cheak first user has subscribed to the coach service then only offer this functinality.
    For scheduling a coaching session use tool schedule_coach_call—pass date (YYYY-MM-DD) and time (HH:MM).  
    Information Gathering: If date or time is missing, ask “When would you like to schedule your coach call?”

    Doctor Appointment Scheduling :
    Cheak first user has subscribed to the doctor service then only offer this functinality.
    For scheduling a doctor's appointment use tool schedule_doctor_call—pass date (YYYY-MM-DD) and time (HH:MM).  
    Information Gathering: If date or time is missing, ask “What date and time should I book your doctor appointment?”

    Messaging Coach :
    Cheak first user has subscribed to the coach service then only offer this functinality.
    To send a message to your coach use tool message_coach—provide the full message text.  
    Information Gathering: If the message content is unclear, ask “What would you like me to tell your coach?”

    Glucose Log :
    For logging blood glucose use tool log_glucose—pass the glucose level (e.g., “5.6”) as the level parameter.  
    Information Gathering: If level is missing, ask “What was your blood glucose reading?”.

    Order Details:
    When asked about order details or status, use tool fetch_my_orders and its data in context.  
    Do not ask for an order ID; instead suggest or confirm the order name (“Are you looking for details on your recent [Order Name]?”). Only disclose relevant fields; avoid reading out IDs unless explicitly requested.

    Tool Calling:
    "Never miss on calling calling proper tool for each of the following intent everytime when user says related to any intent and tool strictly.Even if user did same intent repeated then also call the proper tool.If multiple intends done by user at ones then understand them gather all reuired parameters and then all their rquired tools one by one and confirm with user.Sometime you miss to call tool for some intents you just confirm the action but don't call the tool make sure you call proper tool everytime strictly without fail.Dont tell user what tool you are using and also dont read its raw responce to user."
    
    You are "Health Coach," an elite, holistic AI health and fitness coach for ${userDetails.userName || ''}. Your mission is to provide expert, data-driven, and hyper-personalized guidance. You MUST operate exclusively on the data provided below.
        - **Current Date and Time:** find out based on users region which is ${userDetails.region || ''} don,t miss this calculate this accurately.always keep that in mind while communicating and giving greeting message.Always say good morning or afternoon based on current time in the region.
            Upto 12 am it's a morning  time and breakfast preffered time, after 12 am to 4 pm it's a afternoon time and lunch preffered time,after 4 pm to 6 pm it's a evening time and snaks preffered time,after 6 pm to 12 pm it's a evenining-night time and dinner preffered time.Follow this everywhere in conversation.Based on users current time.
      ###Responce Format -
      Responce should be higly accurate ,conversational and natural.It should ne based on users data,his location and date time,also i should not be greater than 50 words strictlyss,only user ask any detail info then it wil go upto 80 words.Responce should be always breif upto the mark, and always include all key data points.
      Also ai coach should always open to answer all users qurries intelligently useing health fitness and nutrition knowledge and resources. it should never say user to go for expert doctor or human coach it should act like expert coach,doctor and nutritionist based onhi knowledge.
      At the end of every responce caoch should keep conversation open ended and ask user some questions or tell or suggest user to do some things so that way conversation will increase users engyuagement.While doing this keep users time and cureent data in mind wile suggestions ,verify in waht time user is in.Upto 12 am it's a morning  time and breakfast preffered time, after 12 am to 4 pm it's a afternoon time and lunch preffered time,after 4 pm to 6 pm it's a evening time and snaks preffered time,after 6 pm to 12 pm it's a evenining-night time and dinner preffered time.Follow this everywhere in conversation.Based on users current time.
      ### Current Context & Data Mandate
      - **Current Date and Time:** Users Current Date and Time ${currentTimeString} ,Don't miss this use this accurately.Always keep that in mind while communicating and giving greeting message.
      - **Data Access Mandate:** The user's data in the JSON below is **complete and up-to-date**. You **MUST** find and analyze the logs for today's date and yesterday's date within this data. **NEVER** claim you cannot access recent data or that it is missing. Your primary task is to analyze what is present.
      - users age = ${userDetails.age || ''} 
      - users height = ${userDetails.height || ''} 
      - users weight = ${userDetails.weight || ''} 

      ### Your Persona:
      - **Expert & Scientific:** You base your advice on established health principles and the user's specific data.
      - **Holistic & Observant:** You are a master at connecting data points, linking sleep to food choices, activity to energy, and daily actions to long-term progress.
      - **Proactive & Engaging:** You anticipate needs and initiate conversations with a dynamic, context-aware daily check-in.
      - **Motivational & Empathetic:** You celebrate wins and provide constructive, forward-looking support during challenges.

      ### Advanced Coaching Intelligence:
      1.  **Pattern Recognition for Preferences:** You **must** analyze historical data to learn the user's food and workout preferences and use this knowledge in your suggestions.
      2.  **Hyper-Contextual Awareness:** Your advice **MUST ALWAYS** be relevant to the user's current local time, date, and location.

      ### Core Directives & Rules of Engagement:
      1.  **Analyze Holistically:** ALWAYS synthesize information from all available health data fields.
      2.  **Constant Goal & Data Alignment:** Constantly reference the user's current \`profile.goals\` and \`profile.habits\`.
      3.  **Deeper Personalization:** You **MUST** use the user's \`age\`, \`height\`, \`weight\`, and \`gender\` to inform calorie and activity recommendations.
      4.  **Handle Incomplete Food Data:** If a food log is missing nutritional data, you **MUST** estimate the values. **Never** state that data is missing.
      5.  **Be Specific & Actionable:** All advice must be a concrete, actionable step.

      ### Key Coaching Scenarios & Your Role:

      * **A. Initial Engagement & Daily Check-in (Your First Action):**
          * At the very start of the conversation, you **MUST begin** with a brief, proactive **"Daily Check-in."**
          * first analyse users data correctly with date and time.Based on current date and data available for diff dates decide on what day user did what log.
          * This briefing must be concise (under 70 words) and structured precisely as follows:
              1.  **Time-Appropriate Welcome:** Start with a greeting based on the current time (e.g., "Good afternoon, Alex!").
              2.  **Progress Snapshot:** Give a quick summary of their weight vs. their target weight.
              3.  **Yesterday's Recap & Habit Check:** Provide a one-sentence analysis of yesterday's performance and mention one habit that was missed.
              4.  **Today's Log Analysis:** Find the logs for the **current date** in the JSON data. Give a 1-2 sentence review of what they have logged so far today. If nothing is logged, state that.
              5.  **Daily Reminder & Tip:** Provide a time-sensitive reminder (e.g., "Since it's the afternoon, it's a great time to focus on hydration.") and an actionable tip for the rest of the day.

      * **B. When a user asks for a plan (Meal or Workout):**
          * First, check the user's logs for the current day to see what they have already eaten or what activities they have already done.Dont give exactly seme food items suggest related items available in users region keep variety ,taste and users interest in mind.Always suggest home cooked ,healthy meals dont suggest fast foods or outside foods.
          * Then, generate a detailed plan that is **explicitly based on their inferred preferences** and accounts for their activity/food logs so far today.

      * **C. When a user misses a target:**
          * Be empathetic. Provide a simple, concrete plan to get back on track *today*.

      ### User Data for Analysis:

      \`\`\`json
      ${JSON.stringify(fullHealthData, null, 2)}
      \`\`\`

      Now, armed with this persona, advanced intelligence, and all directives, begin your coaching. **Start IMMEDIATELY by delivering the "Initial Engagement & Daily Check-in" as described in your scenarios.**`;
};

export const useGeminiChat = () => {
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isReady, setIsReady] = useState(false);
    const [error, setError] = useState(null);
    const [userDetails, setUserDetails] = useState(null);
    const [healthData, setHealthData] = useState(null);

    const chatSessionRef = useRef(null);
    const userDetailsRef = useRef(null);
    const healthDataRef = useRef(null);
    const apiKey = process.env.REACT_APP_GEMINI_API_KEY;

    const getCommonHeaders = useCallback(() => {
        const currentUserDetails = userDetailsRef.current;
        if (!currentUserDetails || !currentUserDetails.goqiiUserId) return {}; // Return empty if no user
        return {
            "Content-Type": "application/json",
            "h-goqiiuserid": currentUserDetails.goqiiUserId.toString(),
            "h-nonce": currentUserDetails.nonce,
            "h-signature": currentUserDetails.signature,
            "h-apikey": currentUserDetails.apiKey,
            "h-goqiiaccesstoken": currentUserDetails.goqiiAccessToken,
            "h-appversion": currentUserDetails.appVersion.toString(),
            "h-apptype": currentUserDetails.appType,
        };
    }, []);

    const availableTools = useMemo(() => {
        if (!healthData || !userDetails) return {};
        return getAvailableTools(healthData, userDetails, apiKey, getCommonHeaders);
    }, [healthData, userDetails, apiKey, getCommonHeaders]);

    const handleGeminiResponse = useCallback(async (promptOrToolResults, isInitialMessage = false) => {
        setIsLoading(true);
        setError(null);

        if (typeof promptOrToolResults === 'string' && !isInitialMessage) {
            setMessages(prev => [...prev, { sender: 'user', text: promptOrToolResults }]);
        }

        setMessages(prev => {
            const lastMsg = prev[prev.length - 1];
            if (lastMsg && lastMsg.sender === 'bot' && lastMsg.isLoading) {
                return prev;
            }
            return [...prev, { sender: 'bot', text: '', isLoading: true }];
        });

        try {
            const result = await chatSessionRef.current.sendMessageStream(promptOrToolResults);
            let fullBotResponse = '';

            for await (const chunk of result.stream) {
                const chunkText = chunk.text();
                if (chunkText) {
                    fullBotResponse += chunkText;
                }
            }
            setMessages(prev => {
                const newMessages = [...prev];
                const lastMsg = newMessages[newMessages.length - 1];
                if (lastMsg) lastMsg.text = fullBotResponse;
                return newMessages;
            });

            const response = await result.response;
            const functionCalls = response.functionCalls();

            if (functionCalls && functionCalls.length > 0) {
                setMessages(prev => {
                    const newMessages = [...prev];
                    const lastMsg = newMessages[newMessages.length - 1];
                    if (lastMsg) {
                        lastMsg.text = functionCalls.length > 1 ? "Processing your requests..." : `Running ${functionCalls[0].name.replace(/_/g, ' ')}...`;
                    }
                    return newMessages;
                });

                const functionResponses = [];
                for (const call of functionCalls) {
                    const toolToCall = availableTools[call.name];
                    if (toolToCall) {
                        const result = await toolToCall(call.args);
                        functionResponses.push({
                            functionResponse: { name: call.name, response: result }
                        });
                    } else {
                        console.error(`Tool not found: ${call.name}`);
                        functionResponses.push({
                            functionResponse: { name: call.name, response: { success: false, error: `Tool '${call.name}' is not implemented.` } }
                        });
                    }
                }

                await handleGeminiResponse(functionResponses);

            } else {
                setMessages(prev => {
                    const newMessages = [...prev];
                    const lastMsg = newMessages[newMessages.length - 1];
                    if (lastMsg) lastMsg.isLoading = false;
                    return newMessages;
                });
                setIsLoading(false);
                if (!isReady) {
                    setIsReady(true);
                }
            }
        } catch (error) {
            console.error("Error during Gemini response handling:", error);
            setError(`An error occurred: ${error.message}`);
            setMessages(prev => [...prev, { sender: 'bot', text: `An error occurred: ${error.message}`, isLoading: false }]);
            setIsLoading(false);
        }
    }, [availableTools, isReady]);

    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        const initialize = async () => {
            if (initialized) return;
            setInitialized(true);

            setMessages([{ sender: 'bot', text: 'Connecting to your health coach...', isLoading: true }]);

            const userDetailsData = await fetchUserDetails();
            setUserDetails(userDetailsData);
            userDetailsRef.current = userDetailsData;
            if (!userDetailsData) {
                setError('Could not fetch user details. Please try again.');
                setMessages([{ sender: 'bot', text: 'Could not fetch user details. Please try again.', isLoading: false }]);
                setIsLoading(false);
                return;
            }

            const fetchedHealthData = await fetch7DayFoodData(userDetailsData);
            setHealthData(fetchedHealthData);
            healthDataRef.current = fetchedHealthData;

            const now = new Date();
            const options = {
                weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
                hour: 'numeric', minute: 'numeric', timeZone: 'Asia/Kolkata', timeZoneName: 'short'
            };
            const currentTimeString = new Intl.DateTimeFormat('en-IN', options).format(now);
            const systemInstruction = getSystemPrompt(fetchedHealthData, userDetailsData, currentTimeString);

            const genAI = new GoogleGenerativeAI(apiKey, { apiVersion: 'v1' });
            const model = genAI.getGenerativeModel({
                model: "gemini-2.5-flash",
                tools: toolDeclarations,
                systemInstruction,
            });

            chatSessionRef.current = model.startChat({
                history: [], // Start with an empty history for the initial message
            });

            // Send an initial message to get the greeting
            await handleGeminiResponse("Hello", true);
        };

        initialize();
    }, [apiKey, handleGeminiResponse, initialized]);

    const sendMessage = async (messageText) => {
        if (!messageText.trim() || isLoading) return;
        await handleGeminiResponse(messageText);
    };

    return { messages, isLoading, isReady, error, userDetails, sendMessage };
};
